import Service, { inject as service } from '@ember/service';
import { ASSIGNMENT_COMMAND_CONSTANTS } from 'sh-www/services/commands/assignment-commands';
import { ASSIGNMENT } from 'sh-www/models/assignment';
import { APPLICATION_SOURCE } from 'sh-www/models/application';

/**
 * Service to handle assignment manipulation
 */
const AssignmentManager = Service.extend({
  /**
   * @type {Service}
   */
  assignmentCommandService : service('commands/assignment-commands'),
  errorTracker: service('error-tracker'),
  moment: service(),
  store: service(),

  /**
   * @type {Assignment|null}
   */
  _assignment: null,

  /**
   *
   * @param assignment
   * @return {AssignmentManager}
   */
  manage(assignment) {
    this.set('_assignment', assignment);
    return this;
  },

  /**
   * Take in a list of parameters and return an object that has the list of errors as well as an
   * object ready to send to the API to create an assignment.
   *
   * @param {Boolean} isUpdatingExisting
   * @param {String} createdFrom
   * @param {Boolean} isSelect
   * @param {Boolean} isEligibleForSelect
   * @param {Number} productionId
   * @param {Number} companyTeamId
   * @param {String} title
   * @param {String} type     of type ASSIGNMENT_TYPE_*
   * @param {Boolean} isDraft
   * @param {Array<AssignmentSchedule>} scheduleItems
   * @param {Ember.MutableArray<Service>} services
   * @param {number[]} classificationIds
   * @param {string[]} customClassifications
   * @param {Moment} deadline
   * @param {String} description
   * @param {String} deliverables
   * @param {Number} privacyLevel   of type PRIVACY_LEVEL_*
   * @param {Number} compensationMin
   * @param {Number} compensationMax
   * @param {Number} rateType       of type RATE_TYPE_*
   * @param {Boolean} isCoverFeesFieldVisible
   * @param {Boolean} isExpensesFieldVisible
   * @param {Boolean} isExpediteFieldVisible
   * @param {Boolean} isCoverFees
   * @param {Boolean} isCoverExpenses
   * @param {Boolean} isExpedite
   * @param {Boolean} isDirect
   * @param {Number} minRating      b/t 1-5
   * @param {Boolean=} isUrgent
   * @param {Number} estimatedDays
   * @param {String=} opt_examples
   * @param {String=} opt_placeId
   * @param {Number=} opt_regionId
   * @param {Number=} opt_countryId
   * @param {Boolean=} opt_isGlobal
   * @param {String=} opt_candidatePlaceId
   * @param {Number=} opt_candidateCountryId
   * @param {Boolean=} opt_candidateIsGlobal
   * @param {String=} opt_clonedAssignmentId
   * @return {
   *    {
   *      hasErrors: boolean,     // true if we have errors
   *      errorsMap: {},          // error map of key value pairs from ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS
   *      createAssignmentDataMap: {}   // the map of values ready to send to the api from ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS
   *    }}
   */
  validateAndGenerateAssignmentCreateData({
    isUpdatingExisting,
    createdFrom,
    isSelect,
    isEligibleForSelect,
    productionId,
    companyTeamId,
    title,
    type,
    isDraft,
    scheduleItems,
    services,
    classificationIds,
    customClassifications,
    deadline,
    description,
    deliverables,
    privacyLevel,
    compensationMin,
    compensationMax,
    rateType,
    isCoverFeesFieldVisible,
    isExpensesFieldVisible,
    isExpediteFieldVisible,
    isCoverFees,
    isCoverExpenses,
    isExpedite,
    isDirect,
    minRating,
    isUrgent,
    estimatedDays,
    opt_examples = '',
    opt_placeId = '', // mutually exclusive with other location attributes
    opt_regionId = 0, // mutually exclusive with other location attributes
    opt_countryId = 0, // mutually exclusive with other location attributes
    opt_isGlobal = null, // mutually exclusive with other location attributes
    opt_candidatePlaceId = '', // mutually exclusive with other candidate location attributes
    opt_candidateCountryId = 0, // mutually exclusive with other candidate location attributes
    opt_candidateIsGlobal = null, // mutually exclusive with other candidate location attributes
    opt_clonedAssignmentId = null,
    opt_clientUUID = null,
  }) {
    let errorsObj = {};

    if (!title) {
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.TITLE] = 'Title is required.';
    }

    if (type !== ASSIGNMENT.TYPES.STANDARD && type !== ASSIGNMENT.TYPES.PITCH) {
      this.get('errorTracker').error('validateAssignmentCreateData -- no type selected', this);
    }

    if (!isDraft) {
      if (!opt_placeId && !opt_regionId && !opt_countryId && !opt_isGlobal) {
        errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.PLACE] = 'You must select a location for the posting.';
      }

      if (!deadline) {
        errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DEADLINE] = 'You must select a deadline for the posting.';
      }

      if (!description) {
        errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DESCRIPTION] = 'You must add a description for the posting.';
      }

      if (deliverables.length < 1) {
        errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DELIVERABLES] = 'You must add deliverables for the posting.';
      }

      if (scheduleItems) {
        // validate each pair
        const hasProblem = scheduleItems.some((scheduleItem) => {
          return !scheduleItem.date || !scheduleItem.description;
        });

        if (hasProblem) {
          errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SCHEDULE_ITEMS] = 'You must fill out all fields for key dates.';
        }
      }

      if (!compensationMin) {
        if (compensationMin === 0) {
          errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COMPENSATION_MIN] = 'The minimum compensation must be at least 1.';
        } else {
          errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COMPENSATION_MIN] = 'You must select a minimum compensation.';
        }
      }

      if (!compensationMax) {
        errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COMPENSATION_MAX] = 'You must select a maximum compensation.';
      }

      if (isDirect == null) {
        this.get('errorTracker').error('validateAssignmentCreateData -- no direct selected', this);
      }

      // Only validate the following fields if this is not a Select posting.
      if (!services || !services.get('length')) {
        errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SERVICES] = 'You must select at least one service.';
      }

      if (!privacyLevel || !Object.values(ASSIGNMENT.PRIVACY_LEVEL).includes(privacyLevel)) {
        errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.PRIVACY_LEVEL] = 'Privacy level is required.';
      }

      if (!rateType || !Object.values(ASSIGNMENT.RATE_TYPE).includes(rateType)) {
        errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.RATE_TYPE] = 'You must select a rate type.';
      }

      if (isCoverFeesFieldVisible && isCoverFees == null) {
        errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SELLER_FEES] = 'You must select if you will cover creator fees or not.';
      }

      if (isExpensesFieldVisible && isCoverExpenses == null) {
        errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.EXPENSE] = 'You must select if you will cover expenses or not.';
      }

      if (isExpediteFieldVisible && isExpedite == null) {
        errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.EXPEDITE] = 'You must select if you will expedite payments or not.';
      }

      if (!estimatedDays || estimatedDays < 0) {
        errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.ESTIMATED_DAYS] = 'You must add the estimated number of days.';
      }
    }

    if ((compensationMax && compensationMin) && (compensationMax < compensationMin)) {
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COMPENSATION_MAX] = 'You must select a max budget greater than the min budget.';
    }

    if (deadline) {
      let deadlineDateTime = deadline.toDate().getTime();
      if (type === ASSIGNMENT.TYPES.STANDARD) {
        let oneDayLater = this.get('moment').moment().add(1, 'day').hours(23).minutes(59).seconds(59).milliseconds(0);
        if (deadlineDateTime < oneDayLater.toDate().getTime()) {
          errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DEADLINE] = 'Choose a valid date in the future';
        }
      } else {
        let oneWeekLater = this.get('moment').moment().add(7, 'days').hours(23).minutes(59).seconds(59).milliseconds(0);
        if (deadlineDateTime < oneWeekLater.toDate().getTime()) {
          errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DEADLINE] = 'Choose a valid date in the future';
        }
      }
    }

    let serviceIds = [];
    if (services) {
      services.forEach(service => serviceIds.push(service.get('id')));
    }

    // Set Select as true if the posting eligible.
    // Edited postings require Select status change by admin
    const select = (isUpdatingExisting && !isDraft) ? isSelect : isEligibleForSelect;

    let createAssignmentDataMap = {};
    if (!isDraft) {
      createAssignmentDataMap = {
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.TITLE] : title,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.TYPE] : type,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SELECT] : select ? 1 : 0,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DRAFT] : isDraft ? 1 : 0,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.PRODUCTION] : productionId ?? null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COMPANY_TEAM] : companyTeamId,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SCHEDULE_ITEMS] : scheduleItems,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SERVICES] : serviceIds,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.CLASSIFICATIONS] : classificationIds,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.CUSTOM_CLASSIFICATIONS] : customClassifications,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DEADLINE] : deadline ? deadline.toDate() : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DESCRIPTION] : description,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DELIVERABLES] : deliverables,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.PRIVACY_LEVEL] : privacyLevel,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COMPENSATION_MIN] : compensationMin,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COMPENSATION_MAX] : compensationMax,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.MIN_RATING] : minRating,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.ESTIMATED_DAYS] : estimatedDays,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.URGENT] : isUrgent ? 1 : 0,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.RATE_TYPE] : rateType,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SELLER_FEES] : isCoverFees ? 1 : 0,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.EXPENSE] : isCoverExpenses ? 1 : 0,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.EXPEDITE] : isExpedite ? 1 : 0,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DIRECT] : isDirect ? 1 : 0,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.CREATED_FROM]: createdFrom,
      };

      if (opt_examples) {
        createAssignmentDataMap[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.EXAMPLES] = opt_examples;
      }

      if (opt_placeId) {
        createAssignmentDataMap[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.PLACE] = opt_placeId;
      } else if (opt_regionId) {
        createAssignmentDataMap[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.REGION] = opt_regionId;
      } else if (opt_countryId) {
        createAssignmentDataMap[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COUNTRY] = opt_countryId;
      } else if (opt_isGlobal) {
        createAssignmentDataMap[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.GLOBAL] = true;
      }

      if (opt_candidatePlaceId) {
        createAssignmentDataMap[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.CANDIDATE_PLACE] = opt_candidatePlaceId;
      } else if (opt_candidateCountryId) {
        createAssignmentDataMap[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.CANDIDATE_COUNTRY] = opt_candidateCountryId;
      } else if (opt_candidateIsGlobal) {
        createAssignmentDataMap[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.CANDIDATE_GLOBAL] = true;
      }

      if (opt_clonedAssignmentId) {
        createAssignmentDataMap[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.CLONED_ASSIGNMENT_ID] = opt_clonedAssignmentId;
      }
    } else {
      let hasUrgentSet = isUrgent != null,
        hasIsDirectSet = isDirect != null;

      createAssignmentDataMap = {
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.TITLE] : title,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.TYPE] : type,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SELECT] : select ? 1 : 0,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DRAFT] : isDraft ? 1 : 0,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.PRODUCTION] : productionId ?? null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COMPANY_TEAM] : companyTeamId ? companyTeamId : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SCHEDULE_ITEMS] : scheduleItems,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SERVICES] : serviceIds.length ? serviceIds : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.CLASSIFICATIONS] : classificationIds,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.CUSTOM_CLASSIFICATIONS] : customClassifications,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DESCRIPTION] : description ? description : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DELIVERABLES] : deliverables ? deliverables : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.PRIVACY_LEVEL] : privacyLevel ? privacyLevel : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COMPENSATION_MIN] : compensationMin ? compensationMin : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COMPENSATION_MAX] : compensationMax ? compensationMax : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.MIN_RATING] : minRating ? minRating : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.ESTIMATED_DAYS] : estimatedDays ? estimatedDays : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.URGENT] : isUrgent ? 1 : hasUrgentSet ? 0 : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.RATE_TYPE] : rateType ? rateType : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SELLER_FEES] : isCoverFees ? 1 : 0,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.EXPENSE] : isCoverExpenses ? 1 : 0,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.EXPEDITE] : isExpedite ? 1 : 0,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DIRECT] : isDirect ? 1 : hasIsDirectSet ? 0 : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.EXAMPLES] : opt_examples ? opt_examples : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.PLACE] : opt_placeId ? opt_placeId : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.REGION] : opt_regionId ? opt_regionId : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COUNTRY] : opt_countryId ? opt_countryId : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.GLOBAL] : opt_isGlobal ? opt_isGlobal : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.CANDIDATE_PLACE] : opt_candidatePlaceId ? opt_candidatePlaceId : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.CANDIDATE_COUNTRY] : opt_candidateCountryId ? opt_candidateCountryId : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.CANDIDATE_GLOBAL] : opt_candidateIsGlobal ? opt_candidateIsGlobal : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.CLONED_ASSIGNMENT_ID] : opt_clonedAssignmentId ? opt_clonedAssignmentId : null,
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.CREATED_FROM]: createdFrom,
      };

      if (deadline) {
        createAssignmentDataMap[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DEADLINE] = deadline ? deadline.toDate() : null;
      }
    }
    // Add client UUID
    if (opt_clientUUID) {
      createAssignmentDataMap[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.CLIENT_UUID] = opt_clientUUID;
    }

    return {
      hasErrors: !!Object.keys(errorsObj).length,
      errorsMap: errorsObj,
      createAssignmentDataMap: createAssignmentDataMap
    };
  },

  /**
   * Take in a list of parameters and return an object that has the list of errors for the direct
   * hire overview section (title, services, location, and message only).
   *
   * @param {String} title The title of the assignment.
   * @param {Ember.MutableArray<Service>} services The array of services.
   * @param {String} locationPlaceId The place ID of the selected Location.
   * @param {Boolean} locationIsGlobal If no location and explictly set
   * @param {Array} deliverables Deliverables associated with assignment
   * @returns {
   *   hasErrors: boolean, // Whether there are errors in the direct hire overview section.
   *   errorsMap: {}, // Key value pairs from ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.
   * }
   */
  validateDirectHireOverview(
    title,
    services,
    locationPlaceId,
    locationIsGlobal,
    freelancerMessage,
    deliverables,
  ) {
    const errorsObj = {};

    if (!title) {
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.TITLE] = 'Title is required.';
    }

    if (!services || !services.get('length')) {
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SERVICES] = 'You must select at least one service.';
    }

    if (!locationPlaceId && !locationIsGlobal) {
      // If the input field is empty.
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.PLACE] = 'You must specify the location.';
    }

    if (!freelancerMessage) {
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.MESSAGE] = 'You must add a message for the freelancer.';
    }

    if (deliverables.length < 1) {
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DELIVERABLES] = 'You must add deliverables for the posting.';
    }

    return {
      hasErrors: !!Object.keys(errorsObj).length,
      errorsMap: errorsObj,
    };
  },

  /**
   * Take in a list of parameters and return an object that has the list of errors for the direct
   * hire offer section.
   *
   * @param {Number} offerAmount The direct offer amount.
   * @param {Boolean} isExpensesFieldVisible
   * @param {Boolean} willCoverExpenses Whether the buyer will cover expenses.
   * @param {Boolean} isSellerFeesFieldVisible
   * @param {Boolean} isCoverSellerFees Whether the buyer will cover seller fees.
   * @returns {
   *   hasErrors: boolean, // Whether there are errors in the direct hire overview section.
   *   errorsMap: {}, // Key value pairs from ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.
   * }
   */
  validateDirectHireOffer(
      offerAmount,
      isExpensesFieldVisible,
      willCoverExpenses,
      isSellerFeesFieldVisible,
      isCoverSellerFees,
      isExpediteFieldVisible,
      isExpedite,
    ) {
    const errorsObj = {};

    if (!offerAmount) {
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.AMOUNT] = 'You must specify an offer amount.';
    }

    if (isSellerFeesFieldVisible && isCoverSellerFees == null) {
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SELLER_FEES] = 'You must select if you will cover creator fees or not.';
    }

    if (isExpensesFieldVisible && willCoverExpenses == null) {
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.EXPENSE] = 'You must select if you will cover expenses or not.';
    }

    if (isExpediteFieldVisible && isExpedite == null) {
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.EXPEDITE] = 'You must select if you will expedite payments or not.';
    }

    return {
      hasErrors: !!Object.keys(errorsObj).length,
      errorsMap: errorsObj,
    };
  },

  /**
   * Take in a list of parameters and return an object that has the list of errors as well as an
   * object ready to send to the API to create an assignment.
   *
   * @param {number} source
   * @param {String} title
   * @param {Number} direct   of type ASSIGNMENT_COMMAND_CONSTANTS.DIRECT.*
   * @param {Number} productionId,
   * @param {Number} companyTeamId
   * @param {String} freelancerId
   * @param {Boolean} isReadyToMakeOffer    True if the user has selected they are ready to make an offer.
   * @param {Boolean} isComplete    Is the work complete or not.
   * @param {Ember.MutableArray<Service>} services
   * @param {Array<AssignmentDeliverable>} deliverables
   * @param {Array<AssignmentSchedule>} scheduleItems
   * @param {String} freelancerMessage
   * @param {Boolean} isExpensesFieldVisible
   * @param {Boolean} isCoverExpenses
   * @param {Boolean} isSellerFeesFieldVisible
   * @param {Boolean} isCoverSellerFees
   * @param {String} locationPlaceId      The place ID of the selected Location.
   * @param {Boolean} isGlobal            If no place id set and explictly set as global
   * @param {String=} opt_costCode        The code code body
   * @param {String=} opt_storyCode       The story code body
   * @param {String=} opt_reviewComment   The review comment left by buyer
   * @param {Number=} opt_reviewScore     1-5 for the review given to a freelancer from buyer
   * @param {Number=} opt_offerAmount     The offer amount for the direct hire assignment.
   * @param {Boolean=} opt_clientUUID     Client UUID to prevent duplicate posting creation
   * @return {
   *    {
   *      hasErrors: boolean,     // true if we have errors
   *      errorsMap: {},          // error map of key value pairs from ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS
   *      createAssignmentDataMap: {}   // the map of values ready to send to the api from ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS
   *    }}
   */
  validateAndGenerateDirectHireAssignmentCreateData({
    source,
    title,
    direct,
    productionId,
    companyTeamId,
    freelancerId,
    isReadyToMakeOffer,
    isComplete,
    services,
    deliverables,
    scheduleItems,
    freelancerMessage,
    isExpensesFieldVisible,
    isExpediteFieldVisible,
    isCoverExpenses,
    isExpedite,
    isSellerFeesFieldVisible,
    isCoverSellerFees,
    locationPlaceId,
    isGlobal,
    opt_costCode = '',
    opt_storyCode = '',
    opt_reviewComment = '',
    opt_reviewScore = 0,
    opt_offerAmount = 0,
    opt_clientUUID = null,
    opt_estimatedDays = undefined,
  }) {
    let errorsObj = {};

    if (!title) {
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.TITLE] = 'Title is required.';
    }

    if (!freelancerId) {
      this.get('errorTracker').error('validateAndGenerateDirectHireAssignmentCreateData -- no freelancerId selected', this);
    }

    if (!locationPlaceId && !isGlobal) {
      // If the input field is empty.
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.PLACE] = 'You must specify the location.';
    }

    if (!services || !services.get('length')) {
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SERVICES] = 'You must select at least one service.';
    }

    if (deliverables.length < 1) {
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DELIVERABLES] = 'You must add deliverables for the posting.';
    }

    if (opt_estimatedDays != null) {
      if (!Number.isFinite(opt_estimatedDays)) {
        errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.ESTIMATED_DAYS] = 'You must add the estimated number of days.';
      } else if (opt_estimatedDays < 1) {
        errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.ESTIMATED_DAYS] = 'The estimated number of days must be a valid number greater than 1.';
      }
    }

    if (!freelancerMessage) {
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.MESSAGE] = 'You must add a message for the freelancer.';
    }

    if (isReadyToMakeOffer && !opt_offerAmount) {
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.AMOUNT] = 'You must specify an offer amount.';
    }

    if (isComplete) {
      if (!opt_reviewScore) {
        errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.REVIEW_SCORE] = 'You must select a rating for the freelancer.';
      }
    }

    if (isReadyToMakeOffer && isExpensesFieldVisible && isCoverExpenses == null) {
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.EXPENSE] = 'You must select if you will cover expenses or not.';
    }

    if (isReadyToMakeOffer && isExpediteFieldVisible && isExpedite == null) {
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.EXPEDITE] = 'You must select if you will expedite payments or not.';
    }

    if (isReadyToMakeOffer && isSellerFeesFieldVisible && isCoverSellerFees == null) {
      errorsObj[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SELLER_FEES] = 'You must select if you will cover creator fees or not.';
    }

    if (direct == null) {
      this.get('errorTracker').error('validateAssignmentCreateData -- no direct selected', this);
    }

    let serviceIds = [];
    if (services) {
      services.forEach(service => serviceIds.push(service.get('id')));
    }

    if (!isExpensesFieldVisible) {
      isCoverExpenses = false;
    }

    if (!isExpediteFieldVisible) {
      isExpedite = false;
    }

    if (!isSellerFeesFieldVisible && isCoverSellerFees == null) {
      isCoverSellerFees = false;
    }

    const createdFrom = ASSIGNMENT_COMMAND_CONSTANTS.CREATED_FROM.DIRECT;

    let createAssignmentDataMap = {
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.TITLE] : title,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SELECT]: 0,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.TYPE] : ASSIGNMENT.TYPES.STANDARD,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DIRECT] : direct,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DRAFT] : 0,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.PRODUCTION] : productionId ?? null,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COMPANY_TEAM] : companyTeamId,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SERVICES] : serviceIds,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DELIVERABLES]: deliverables,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SCHEDULE_ITEMS]: scheduleItems,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DEADLINE] : new Date(),
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.PRIVACY_LEVEL] : ASSIGNMENT.PRIVACY_LEVEL.PRIVATE,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.URGENT] : 0,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.RATE_TYPE] : ASSIGNMENT.RATE_TYPE.FLAT,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.EXPENSE] : isCoverExpenses ? 1 : 0,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.EXPEDITE] : isExpedite ? 1 : 0,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SELLER_FEES] : isCoverSellerFees ? 1 : 0,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.PLACE] : locationPlaceId,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.CREATED_FROM]: createdFrom,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DESCRIPTION]: freelancerMessage, // stored 2 places
    };

    if (locationPlaceId) {
      createAssignmentDataMap[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.PLACE] = locationPlaceId;
    } else if (isGlobal) {
      createAssignmentDataMap[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.GLOBAL] = true;
    }

    createAssignmentDataMap[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DIRECT_INFO] = {
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.AMOUNT] : opt_offerAmount ? opt_offerAmount : 0,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COMPANY_TEAM] : companyTeamId,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DIRECT_SERVICE_IDS] : serviceIds,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.FREELANCER_ID] : freelancerId,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.IS_COMPLETE] : !!isComplete,
      [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.SOURCE] : source,
    };

    // quotes get a project summary as the first thread item, so don't send the message separately
    if (source !== APPLICATION_SOURCE.QUOTE) {
      createAssignmentDataMap[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.MESSAGE] = freelancerMessage;
    }

    let directInfo = createAssignmentDataMap[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DIRECT_INFO];
    if (opt_costCode) {
      directInfo[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COST_CODE] = opt_costCode;
    }

    if (opt_estimatedDays) {
      createAssignmentDataMap[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.ESTIMATED_DAYS] = opt_estimatedDays;
    }

    if (opt_storyCode) {
      directInfo[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.STORY_CODE] = opt_storyCode;
    }

    if (opt_reviewScore) {
      directInfo[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.REVIEW_SCORE] = opt_reviewScore;
    }

    if (opt_reviewComment) {
      directInfo[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.REVIEW_COMMENT] = opt_reviewComment;
    }

    // Add client UUID
    if (opt_clientUUID) {
      createAssignmentDataMap[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.CLIENT_UUID] = opt_clientUUID;
    }

    return {
      hasErrors: !!Object.keys(errorsObj).length,
      errorsMap: errorsObj,
      createAssignmentDataMap: createAssignmentDataMap
    };
  }
});

export default AssignmentManager;
